import { FC, Fragment, useCallback, useEffect, useState } from 'react';
import { formatCurrency, formatDate, toOrdinalString } from '@lib/utils.ts';
import { Button, Card, CardRanking, Divider } from '@features/ui';
import { useTranslation } from 'react-i18next';
import { RequestDialog } from '@features/user/payoutRequest/requestDialog';
import {
  PaAccounts,
  PayoutRequest,
  TPaAccountHistory,
  TPaAccountHistoryPayout,
} from '@src/types';
import {
  PayoutDialog,
  PayoutFormData,
} from '@features/admin/payoutRequests/PayoutPopup.tsx';
import { getPaAccountsHistories, requestPayout } from '@src/services/payout.ts';

interface StatusCardProps {
  payout: TPaAccountHistoryPayout;
}

const StatusCard: FC<StatusCardProps> = ({ payout }) => {
  const { t } = useTranslation();

  return (
    <div className={'flex flex-col'}>
      <div className={'flex w-full justify-between text-sm font-bold'}>
        <div className={'flex-1'} />
        <div className={'flex-1 flex justify-between'}>
          <div className={'text-[#FFD000] text-[10px]'}>
            <p className={'text-xs'}>{t('payoutRequest.created')}</p>
            <p>{payout.created_at ? formatDate(payout.created_at) : '-'}</p>
          </div>
          <div className={'text-[#FF8000] text-[10px]'}>
            <p className={'text-xs'}>{t('payoutRequest.approved')}</p>
            <p>{payout.approved_on ? formatDate(payout.approved_on) : '-'}</p>
          </div>
          <div className={'text-[#15BA6D] text-[10px]'}>
            <p className={'text-xs'}>{t('payoutRequest.paid')}</p>
            <p>{payout.paid_on ? formatDate(payout.paid_on) : '-'}</p>
          </div>
        </div>
      </div>
      <div className={'flex'}>
        <div className={'text-xs'}>
          <p>${formatCurrency(Number(payout.amount ?? 0), 2)}</p>
          <p className={'text-[10px]'}>{payout.payout_method?.toUpperCase()}</p>
        </div>
        <Divider className={'flex flex-1'} />
      </div>
    </div>
  );
};

interface PaAccountsProps {
  data: PaAccounts[];
  isLoading?: boolean;
}
export const PaAccountsPage: FC<PaAccountsProps> = ({
  data,
  isLoading = false,
}) => {
  const [open, setOpen] = useState(false);
  const [submitDone, setSubmitDone] = useState(false);
  const [histories, setHistories] = useState<TPaAccountHistory[]>([]);
  const [isHistoriesLoading, setIsHistoriesLoading] = useState(false);
  const [paAccountSelected, setPaAccountSelected] = useState<PaAccounts | null>(
    null
  );

  const { t } = useTranslation();

  const fetchPaHistories = useCallback(async () => {
    setIsHistoriesLoading(true);

    const response = await getPaAccountsHistories();

    setHistories(response.data ?? []);

    setIsHistoriesLoading(false);
  }, []);

  useEffect(() => {
    fetchPaHistories().then();
  }, [fetchPaHistories]);

  const handleRequest = useCallback((item: PaAccounts) => {
    setPaAccountSelected(item);
    setOpen(true);
  }, []);

  const handleSubmit = useCallback(
    async (data: PayoutFormData) => {
      if (!paAccountSelected) return;
      const payload: PayoutRequest = {
        leeloo_user_id: paAccountSelected.user_id,
        leeloo_account_id: paAccountSelected.id,
        payout_amount: data.amount,
        payout_method: data.paymentMethod,
      };

      await requestPayout(payload);
      setPaAccountSelected(null);
      setSubmitDone(true);
      await fetchPaHistories();
    },
    [fetchPaHistories, paAccountSelected]
  );

  if (isLoading) {
    return (
      <Card className={'gap-y-4 pb-55'}>
        <p className={'text-lg text-muted-foreground'}>
          {t('payoutRequest.loadingDescription')}
        </p>
      </Card>
    );
  }

  if (!data.length) {
    return (
      <Card className={'gap-y-4 pb-55'}>
        <p className={'text-lg'}>{t('payoutRequest.emptyDescription')}</p>
      </Card>
    );
  }

  return (
    <Card className={'gap-y-4 pb-55'}>
      {data.map((item) => {
        const payoutLogic = item.payout_logics.find(
          (payoutLogicItem) =>
            payoutLogicItem.payout_number === item.payout_status.payout_number
        );

        const isTradingDays =
          item.payout_status.trading_days_since_last_payout >
          Number(payoutLogic?.payout_request_after_days);

        const isProfit =
          Number(item.payout_status.profit_current) >
          Number(payoutLogic?.min_payout);

        const isRule =
          Number(item.payout_status.thirty_percent_profit_rule_rate) >
          Number(item.payout_status.thirty_percent_profit_rule);

        const isCompleted = isTradingDays && isProfit && isRule;

        return (
          <Fragment key={item.id}>
            <div className={'flex justify-between'}>
              <div className={'flex flex-1 flex-col gap-y-2'}>
                <p className={'text-sm font-bold'}>
                  {item.rithmic_account_name}
                </p>
                <div className={'flex flex-col gap-y-1'}>
                  <p className={'text-xs'}>{item.product.title}</p>
                  {item.addons?.map((addonItem) => (
                    <p key={addonItem.id} className={'text-xs'}>
                      {addonItem.title}
                    </p>
                  ))}
                </div>
              </div>
              <div className={'flex flex-1 flex-col gap-y-2'}>
                <p className={'text-sm font-bold'}>
                  {t('payoutRequest.tradingDays2', {
                    value: item.trading_days,
                  })}
                </p>
                <p className={'text-sm font-bold'}>
                  {t('payoutRequest.balance', {
                    value: `$${formatCurrency(Number(item?.account_balance ?? 0), 2)}`,
                  })}
                </p>
                <p className={'text-xs '}>
                  {formatDate(item.account_balance_date ?? '')}
                </p>
              </div>
              <div className={'flex flex-1 gap-x-5 items-center'}>
                <div className={'flex flex-col gap-y-1.5 w-[339px]'}>
                  <div
                    className={
                      'text-sm flex flex-1 justify-between px-2.5 gap-x-2'
                    }
                  >
                    <p>
                      Requirements for your{' '}
                      {toOrdinalString(payoutLogic?.payout_number ?? 1)} Payout
                    </p>
                    <p className={'w-13'}>{t('payoutRequest.actual')}</p>
                  </div>
                  <div className={'flex flex-col flex-1 text-xs'}>
                    <CardRanking
                      bgColor={isTradingDays ? '#14CA74' : '#C72E2E'}
                      opacity={isTradingDays ? 0.9 : 0.6}
                    >
                      <div className={'flex justify-between py-1 px-2.5'}>
                        <p>
                          {payoutLogic?.payout_request_after_days}+ Trading Days
                          {payoutLogic?.daily_profit_required
                            ? `, with daily profit of ${payoutLogic?.daily_profit_required_amount ?? 0}`
                            : ''}
                        </p>
                        <p className={'w-13'}>
                          {t('payoutRequest.days', {
                            day: item.payout_status
                              .trading_days_since_last_payout,
                          })}
                        </p>
                      </div>
                    </CardRanking>
                    <CardRanking
                      bgColor={isProfit ? '#14CA74' : '#C72E2E'}
                      opacity={isProfit ? 0.7 : 0.9}
                    >
                      <div className={'flex justify-between py-1 px-2.5'}>
                        <p>Profit #{payoutLogic?.min_payout ?? 0}+</p>
                        <p className={'w-13'}>
                          $
                          {formatCurrency(
                            Number(item.payout_status.profit_current ?? 0),
                            2
                          )}
                        </p>
                      </div>
                    </CardRanking>
                    <CardRanking
                      bgColor={isRule ? '#14CA74' : '#C72E2E'}
                      opacity={isRule ? 0.6 : 0.7}
                    >
                      <div className={'flex justify-between py-1 px-2.5'}>
                        <p>
                          {item.payout_status.thirty_percent_profit_rule_rate}%
                        </p>
                        <p className={'w-13'}>
                          {item.payout_status.thirty_percent_profit_rule}%
                        </p>
                      </div>
                    </CardRanking>
                  </div>
                  <div className={'text-xs'}>
                    {isCompleted
                      ? t('payoutRequest.note2', {
                          amount: formatCurrency(
                            item.payout_status.max_payout_amount,
                            2
                          ),
                          date: formatDate(
                            item.payout_status
                              .earliest_next_allowed_payout_date,
                            true
                          ),
                        })
                      : t('payoutRequest.note1')}
                  </div>
                </div>
                {isCompleted ? (
                  <Button
                    className={
                      'bg-[#198755] w-[150px] text-xs hover:bg-[#489B63]'
                    }
                    size={'xs'}
                    onClick={() => handleRequest(item)}
                  >
                    {t('payoutRequest.eligible')}
                  </Button>
                ) : (
                  <Button
                    className={
                      'bg-[#993231] w-[150px] text-xs hover:bg-[#993231] cursor-default'
                    }
                    size={'xs'}
                  >
                    {t('payoutRequest.notEligible')}
                  </Button>
                )}
              </div>
            </div>
            <Divider />
          </Fragment>
        );
      })}

      {isHistoriesLoading ? (
        <div className={'flex flex-col w-[70%]'}>
          <p className={'text-sm text-muted-foreground'}>
            {t('payoutRequest.historiesLoading')}
          </p>
        </div>
      ) : histories.length ? (
        histories.map((history) => {
          const sortedPayouts = [...(history.payouts ?? [])].sort(
            (a: TPaAccountHistoryPayout, b: TPaAccountHistoryPayout) =>
              new Date(b.created_at ?? '').getTime() -
              new Date(a.created_at ?? '').getTime()
          );

          return (
            <div
              key={history.leeloo_account_id}
              className={'flex flex-col w-[70%]'}
            >
              <div
                className={'flex w-full justify-between text-sm font-bold mb-4'}
              >
                <div className={'flex-1'}>
                  {history.leeloo_account?.rithmic_account_name}
                </div>
                <div className={'flex-1'}>{t('payoutRequest.status')}</div>
              </div>
              {sortedPayouts.map((payout) => (
                <StatusCard key={payout.id} payout={payout} />
              ))}
            </div>
          );
        })
      ) : (
        <div className={'flex flex-col w-[70%]'}>
          <p className={'text-sm text-muted-foreground'}>
            {t('payoutRequest.historiesEmpty')}
          </p>
        </div>
      )}
      <PayoutDialog
        fromAmount={paAccountSelected?.payout_status.min_payout_amount}
        open={open}
        toAmount={paAccountSelected?.payout_status.max_payout_amount}
        onOpenChange={setOpen}
        onSubmit={handleSubmit}
      />
      <RequestDialog open={submitDone} onOpenChange={setSubmitDone} />
    </Card>
  );
};
